import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { ClipboardCheck, Plus, Trash2, X, AlertTriangle, CheckCircle2, Clock, CalendarDays, Sparkles, Loader2 } from "lucide-react";
import ContextualImportButton from "@/components/ContextualImportButton";
import ContextButton from "@/components/ContextButton";
import DataStateTrendBadge from "@/components/DataStateTrendBadge";
import SourceCalcBtn from "@/components/SourceCalcBtn";
import DocAttachTrigger from "@/components/DocAttachTrigger";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import AiText from "@/components/AiText";
import { notify, fmtError } from "@/lib/notify";

const AUDITS_IMPORT_FIELDS = [
  { key: "titre", label: "Titre", required: true },
  { key: "type", label: "Type", default: "interne" },
  { key: "referentiel", label: "Référentiel", default: "ISO 9001" },
  { key: "date_audit", label: "Date", default: "" },
  { key: "auditeur", label: "Auditeur", default: "" },
  { key: "statut", label: "Statut", default: "planifie" },
  { key: "nb_nc", label: "Nb NC", type: "int", default: 0 },
];

const STATUS_COLOR = {
  planifie: "#A1A1AA",
  en_cours: "#378ADD",
  non_conformite: "#E24B4A",
  cloture: "#1D9E75",
};
const STATUS_ICON = {
  planifie: CalendarDays,
  en_cours: Clock,
  non_conformite: AlertTriangle,
  cloture: CheckCircle2,
};

export default function Audits() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    titre: "", type: "interne", referentiel: "ISO 9001", date_audit: today,
    auditeur: "", statut: "planifie", nb_nc: 0, commentaire: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/workspace/audits")
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || t("saas.crud.error")));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submitAdd = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workspace/audits", { ...form, nb_nc: parseInt(form.nb_nc, 10) || 0 });
      setShowAdd(false);
      setForm({ ...form, titre: "", auditeur: "", commentaire: "", nb_nc: 0 });
      await load();
    } catch (err) { notify.error(fmtError(err, t("saas.crud.error"))); }
    finally { setBusy(false); }
  };

  const updateStatus = async (id, statut) => {
    try { await api.patch(`/workspace/audits/${id}`, { statut }); await load(); }
    catch (err) { notify.error(fmtError(err, t("saas.crud.error"))); }
  };

  const removeAudit = async (id) => {
    if (!window.confirm(t("saas.audits.audits_delete_confirm"))) return;
    try { await api.delete(`/workspace/audits/${id}`); await load(); }
    catch (err) { notify.error(fmtError(err, t("saas.crud.error"))); }
  };

  const [aiBusy, setAiBusy] = useState(null);
  const [aiReading, setAiReading] = useState("");
  const runSparkle = async (a) => {
    setAiBusy(a.id);
    try {
      const r = await api.post("/workspace/ai/sparkle", { module: "audits", entity_id: a.id });
      setAiReading(r.data?.ai_reading || "");
    } catch (err) { notify.error(fmtError(err, "Lecture IA indisponible.")); }
    finally { setAiBusy(null); }
  };

  if (error) return <div className="text-[#E24B4A] text-sm" data-testid="audits-error">{error}</div>;
  if (!data) return <div className="text-zinc-400">{t("saas.crud.loading")}</div>;

  const TYPE_KEYS = ["interne", "externe", "surveillance", "certification"];
  const STATUS_KEYS = ["planifie", "en_cours", "non_conformite", "cloture"];

  return (
    <div className="space-y-6" data-testid="audits-root">
      <ThreeQPageHeader endpoint="/workspace/audits/top-priority" />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#1D9E75] noam-overline">
            <ClipboardCheck className="w-3 h-3" /> {t("saas.audits.audits_overline_strategic")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.audits.title")}</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            {t("saas.audits.audits_subtitle_long")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ContextButton moduleKey="audits" />
          <ContextualImportButton moduleKey="audits" targetFields={AUDITS_IMPORT_FIELDS} onImported={async () => { await load(); }} />
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="audits-add-btn">
            <Plus className="w-4 h-4" /> {t("saas.audits.audits_add_btn")}
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="noam-card p-5" data-testid="audits-stat-total">
          <div className="text-xs text-zinc-400">{t("saas.audits.audits_kpi_total")}</div>
          <div className="noam-display text-3xl font-bold mt-3">{data.total}</div>
        </div>
        <div className="noam-card p-5" data-testid="audits-stat-planifie">
          <div className="text-xs text-zinc-400">{t("saas.audits.audits_kpi_planifie")}</div>
          <div className="noam-display text-3xl font-bold mt-3 text-zinc-300">{data.by_status.planifie}</div>
        </div>
        <div className="noam-card p-5" data-testid="audits-stat-encours">
          <div className="text-xs text-zinc-400">{t("saas.audits.audits_kpi_encours")}</div>
          <div className="noam-display text-3xl font-bold mt-3 text-[#378ADD]">{data.by_status.en_cours}</div>
        </div>
        <div className="noam-card p-5 relative" data-testid="audits-stat-nc">
          <SourceCalcBtn kpiKey="audits_nc_ouvertes" />
          <div className="text-xs text-zinc-400">{t("saas.audits.audits_kpi_nc_open")}</div>
          <div className="flex items-end justify-between mt-3 gap-2">
            <div className="noam-display text-3xl font-bold text-[#E24B4A]">{data.nc_open_count}</div>
            <DataStateTrendBadge
              data={data.nc_open_count}
              etat={data.nc_open_count === 0 ? "bon" : data.nc_open_count <= 3 ? "attention" : "critique"}
              tendance="stable"
              polarity="negative"
              compact
            />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1">{data.by_status.non_conformite} {t("saas.audits.audits_kpi_nc_open_hint")}</div>
        </div>
      </div>

      {data.is_empty ? (
        <div className="noam-card p-12 text-center">
          <ClipboardCheck className="w-12 h-12 text-[#1D9E75] mx-auto opacity-60" />
          <div className="noam-display text-xl mt-4">{t("saas.audits.audits_empty_title")}</div>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.audits.audits_empty_help")}
          </p>
          <button onClick={() => setShowAdd(true)} className="btn-primary mt-6 mx-auto inline-flex" data-testid="audits-empty-add">
            <Plus className="w-4 h-4" /> {t("saas.audits.audits_empty_cta")}
          </button>
        </div>
      ) : (
        <div className="noam-card overflow-hidden" data-testid="audits-table">
          <div className="px-5 py-4 border-b border-white/5">
            <div className="noam-overline">{t("saas.audits.audits_program_overline")}</div>
            <div className="noam-display text-base font-semibold">{t("saas.audits.audits_program_title")}</div>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 border-b border-white/5 bg-white/[0.02]">
              <tr>
                <th className="text-left py-2.5 px-5 font-normal">{t("saas.audits.audits_col_titre")}</th>
                <th className="text-left font-normal">{t("saas.audits.audits_col_referentiel")}</th>
                <th className="text-left font-normal hidden md:table-cell">{t("saas.audits.audits_col_type")}</th>
                <th className="text-left font-normal hidden md:table-cell">{t("saas.audits.audits_col_date")}</th>
                <th className="text-left font-normal hidden lg:table-cell">{t("saas.audits.audits_col_auditeur")}</th>
                <th className="text-center font-normal">{t("saas.audits.audits_col_nc")}</th>
                <th className="text-left font-normal">{t("saas.audits.audits_col_statut")}</th>
                <th className="text-center font-normal w-12"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((a) => {
                const color = STATUS_COLOR[a.statut] || STATUS_COLOR.planifie;
                return (
                  <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-3 px-5 text-white">{a.titre}</td>
                    <td className="text-zinc-300">
                      <span className="text-[10px] px-2 py-0.5 rounded font-semibold bg-white/5 border border-white/10">
                        {a.referentiel}
                      </span>
                    </td>
                    <td className="text-zinc-400 hidden md:table-cell">
                      {t(`saas.audits.types.${a.type}`, { defaultValue: a.type })}
                    </td>
                    <td className="text-zinc-400 hidden md:table-cell">{a.date_audit}</td>
                    <td className="text-zinc-400 hidden lg:table-cell">{a.auditeur || "—"}</td>
                    <td className="text-center">
                      {a.nb_nc > 0
                        ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#E24B4A]/15 text-[#E24B4A] font-semibold">{a.nb_nc}</span>
                        : <span className="text-zinc-600">—</span>}
                    </td>
                    <td>
                      <select
                        value={a.statut}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        className="bg-black/30 border border-white/10 rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[#378ADD]"
                        style={{ color }}
                        data-testid={`audits-status-${a.id}`}
                      >
                        {(data.available_statuses || STATUS_KEYS).map((s) => (
                          <option key={s} value={s}>{t(`saas.audits.statuts.${s}`, { defaultValue: s })}</option>
                        ))}
                      </select>
                    </td>
                    <td className="text-center">
                      <div className="inline-flex items-center gap-2">
                        <DocAttachTrigger module="audits" entityId={a.id} entityLabel={a.titre} />
                        <button
                          onClick={() => runSparkle(a)}
                          disabled={aiBusy === a.id}
                          className="text-zinc-500 hover:text-[#EF9F27] p-1 disabled:opacity-30"
                          data-testid={`audits-sparkle-${a.id}`}
                          title="Lecture IA"
                        >
                          {aiBusy === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => removeAudit(a.id)} className="text-zinc-500 hover:text-[#E24B4A] p-1" data-testid={`audits-del-${a.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="noam-card w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="audits-add-modal">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="noam-overline">{t("saas.audits.audits_modal_overline")}</div>
                <div className="noam-display text-lg font-semibold">{t("saas.audits.audits_new_modal_title")}</div>
              </div>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submitAdd} className="space-y-4">
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_titre_label")}</label>
                <input required value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} placeholder={t("saas.audits.audits_titre_ph")} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="audits-form-titre" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.type_label")}</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="audits-form-type">
                    {TYPE_KEYS.map((k) => <option key={k} value={k}>{t(`saas.audits.types.${k}`, { defaultValue: k })}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_referentiel_label")}</label>
                  <select value={form.referentiel} onChange={(e) => setForm({ ...form, referentiel: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="audits-form-ref">
                    {(data.available_referentiels || []).map((r) => <option key={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_planning_field")}</label>
                  <input type="date" required value={form.date_audit} onChange={(e) => setForm({ ...form, date_audit: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="audits-form-date" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_auditeur_label")}</label>
                  <input value={form.auditeur} onChange={(e) => setForm({ ...form, auditeur: e.target.value })} placeholder={t("saas.audits.audits_auditeur_ph")} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="audits-form-auditeur" />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_initial_status")}</label>
                  <select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="audits-form-statut">
                    {STATUS_KEYS.map((k) => <option key={k} value={k}>{t(`saas.audits.statuts.${k}`, { defaultValue: k })}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_nc_count")}</label>
                  <input type="number" min="0" value={form.nb_nc} onChange={(e) => setForm({ ...form, nb_nc: e.target.value })} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white" data-testid="audits-form-nc" />
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-400 mb-1 block">{t("saas.audits.audits_comment_label")}</label>
                <textarea rows={2} value={form.commentaire} onChange={(e) => setForm({ ...form, commentaire: e.target.value })} placeholder={t("saas.audits.audits_comment_ph")} className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white resize-none focus:border-[#378ADD] focus:outline-none" data-testid="audits-form-comment" />
              </div>
              <button type="submit" disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="audits-form-submit">
                {busy ? t("saas.crud.saving") : t("saas.audits.audits_submit")}
              </button>
            </form>
          </div>
        </div>
      )}
      {aiReading && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAiReading("")} data-testid="audits-sparkle-result">
          <div className="noam-card max-w-2xl w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-[#EF9F27]" />
                <div className="noam-overline">Lecture IA · Audit</div>
              </div>
              <button onClick={() => setAiReading("")} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <pre className="whitespace-pre-wrap text-sm text-zinc-200 font-sans leading-relaxed max-h-[60vh] overflow-y-auto"><AiText>{aiReading}</AiText></pre>
          </div>
        </div>
      )}
    </div>
  );
}
